import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { rotatedBounds, unionRects, type Point, type Rect } from '../../doc/geometry';
import { MIN_ITEM_SIZE, type Geometry, type Item } from '../../doc/schema';
import { commitTransient, withTransient } from '../actions';
import { useBoard, useItemsVersion, usePane, usePaneStore, useReadOnly } from '../context';
import { screenBox, screenToWorld } from '../viewport/camera';
import {
  CORNER_HANDLES,
  RESIZE_HANDLES,
  resizeGeometry,
  rotateGeometry,
  scaleGroup,
  type Handle,
} from './transforms';
import { collectCandidates, snapResize, snapScalePointer, snapThreshold, type SnapCandidates } from './snapping';
import { toolSettings } from '../toolSettings';
import { expandRect } from '../../doc/geometry';
import { visibleWorldRect } from '../viewport/camera';

type Drag =
  | { kind: 'resize'; pointerId: number; item: Geometry; id: string; handle: Handle; snap: SnapCandidates | null }
  | { kind: 'rotate'; pointerId: number; item: Geometry; id: string; startWorld: Point }
  | { kind: 'scale'; pointerId: number; starts: Map<string, Geometry>; bbox: Rect; handle: Handle; snap: SnapCandidates | null };

/**
 * Selection box and handles, drawn in screen space (a sibling of the world layer) so handle
 * sizes stay constant at any zoom. Single selection gets resize + rotate; multi gets corner scale.
 */
export function SelectionOverlay() {
  const board = useBoard();
  const pane = usePaneStore();
  useItemsVersion();
  const selection = usePane((s) => s.selection);
  const camera = usePane((s) => s.camera);
  const transient = usePane((s) => s.transient);
  const editingId = usePane((s) => s.editingId);
  const readOnly = useReadOnly();
  const drag = useRef<Drag | null>(null);

  const items: Item[] = [];
  for (const id of selection) {
    const item = board.index.get(id);
    if (item) items.push(withTransient(item, transient[id]));
  }
  if (!items.length) return null;

  const toWorld = (e: ReactPointerEvent): Point => {
    const host = (e.currentTarget as HTMLElement).closest('.viewport');
    const r = host?.getBoundingClientRect();
    const local = r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: e.clientX, y: e.clientY };
    return screenToWorld(pane.getState().camera, local);
  };

  const begin = (e: ReactPointerEvent, d: Drag) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = d;
  };

  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const world = toWorld(e);
    const s = pane.getState();
    if (d.kind === 'resize') {
      const isImage = board.index.get(d.id)?.type === 'image';
      const keepAspect = isImage ? !e.altKey : e.shiftKey;
      let g = resizeGeometry(d.item, d.handle, world, { keepAspect, minSize: MIN_ITEM_SIZE });
      let gx: number | null = null;
      let gy: number | null = null;
      // Snap the dragged edges of an unrotated resize (Alt disables snapping, as when moving).
      if (d.snap && !d.item.rotation && !e.altKey) {
        const grid = toolSettings.getState().snapGrid ? ((board.meta.get('gridSize') as number | undefined) ?? 32) : null;
        const r = snapResize(d.handle, g, d.snap, snapThreshold(s.camera.zoom), grid, keepAspect, MIN_ITEM_SIZE);
        g = r.geometry;
        gx = r.gx;
        gy = r.gy;
      }
      s.setSnapGuides({ x: gx, y: gy });
      s.setTransient({ [d.id]: g });
    } else if (d.kind === 'rotate') {
      const rotation = rotateGeometry(d.item, d.startWorld, world, e.shiftKey ? 15 : null);
      s.setTransient({ [d.id]: { rotation } });
    } else {
      let t = scaleGroup(d.starts, d.bbox, d.handle, world, MIN_ITEM_SIZE);
      let gx: number | null = null;
      let gy: number | null = null;
      if (d.snap && !e.altKey) {
        const [first] = d.starts.keys();
        const scaled = first ? t[first] : undefined;
        const start = first ? d.starts.get(first) : undefined;
        const scale = scaled?.w && start?.w ? scaled.w / start.w : 1;
        const grid = toolSettings.getState().snapGrid ? ((board.meta.get('gridSize') as number | undefined) ?? 32) : null;
        const r = snapScalePointer(d.bbox, d.handle, scale, d.snap, snapThreshold(s.camera.zoom), grid);
        if (r) {
          t = scaleGroup(d.starts, d.bbox, d.handle, r.pointer, MIN_ITEM_SIZE);
          gx = r.gx;
          gy = r.gy;
        }
      }
      s.setSnapGuides({ x: gx, y: gy });
      s.setTransient(t);
    }
  };

  const onUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    pane.getState().setSnapGuides({ x: null, y: null });
    commitTransient({ board, pane });
  };

  const single = items.length === 1 ? items[0] : undefined;
  const anyLocked = readOnly || items.some((i) => board.isLocked(i));

  if (single) {
    const showHandles = !readOnly && editingId !== single.id && !board.isLocked(single);
    return (
      <div
        className={`sel-box${anyLocked ? ' sel-box-locked' : ''}`}
        style={{ ...screenBox(camera, single), transform: `rotate(${single.rotation + camera.rot}deg)` }}
      >
        {showHandles &&
          RESIZE_HANDLES.map((hd) => (
            <div
              key={`${hd.sx},${hd.sy}`}
              className="handle"
              data-cursor={cursorFor(hd)}
              style={{ left: `${(hd.sx + 1) * 50}%`, top: `${(hd.sy + 1) * 50}%` }}
              onPointerDown={(e) => {
                const s = pane.getState();
                const snap = toolSettings.getState().snap
                  ? collectCandidates(board.index, expandRect(visibleWorldRect(s.camera, s.size), 0.5), new Set([single.id]))
                  : null;
                begin(e, { kind: 'resize', pointerId: e.pointerId, item: single, id: single.id, handle: hd, snap });
              }}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
            />
          ))}
        {showHandles && (
          <div
            className="handle handle-rotate"
            onPointerDown={(e) =>
              begin(e, {
                kind: 'rotate',
                pointerId: e.pointerId,
                item: single,
                id: single.id,
                startWorld: toWorld(e),
              })
            }
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />
        )}
      </div>
    );
  }

  const bbox = unionRects(items.map(rotatedBounds));
  if (!bbox) return null;
  return (
    <div
      className={`sel-box sel-box-multi${anyLocked ? ' sel-box-locked' : ''}`}
      style={{ ...screenBox(camera, bbox), transform: `rotate(${camera.rot}deg)` }}
    >
      {!anyLocked &&
        CORNER_HANDLES.map((hd) => (
          <div
            key={`${hd.sx},${hd.sy}`}
            className="handle"
            data-cursor={cursorFor(hd)}
            style={{ left: `${(hd.sx + 1) * 50}%`, top: `${(hd.sy + 1) * 50}%` }}
            onPointerDown={(e) => {
              const starts = new Map<string, Geometry>();
              for (const i of items) starts.set(i.id, i);
              const st = pane.getState();
              const snap = toolSettings.getState().snap
                ? collectCandidates(board.index, expandRect(visibleWorldRect(st.camera, st.size), 0.5), new Set(starts.keys()))
                : null;
              begin(e, { kind: 'scale', pointerId: e.pointerId, starts, bbox, handle: hd, snap });
            }}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />
        ))}
    </div>
  );
}

function cursorFor(h: Handle): string {
  if (h.sx && h.sy) return h.sx === h.sy ? 'nwse' : 'nesw';
  return h.sx ? 'ew' : 'ns';
}
